<template>
  <div :id="id + '_rep'" class="cat-search-results">
    <ul
      v-if="tabs"
      class="tab-links cat-tabs-container"
      role="tablist"
      :aria-orientation="orientation"
    >
      <li
        v-for="(tab, index) in tabs"
        :key="index + 1"
        class="cat-tab-wrapper"
        :class="_searchTab(tab.value, index)"
        role="presentation"
      >
        <a
          :id="id + '_tab_' + index"
          class="tab-focus"
          :class="'cat-tab cat-tab-' + index"
          :aria-selected="isSelected(tab.value, index) ? 'true' : 'false'"
          tabindex="0"
          role="tab"
          :aria-controls="id ? id + '_tab_panel' : null"
          :data-button="tab.text"
          @click="tabSelect(tab.value, index)"
          @keyup.enter="tabSelect(tab.value, index)"
          @keydown.space.prevent="tabSelect(tab.value, index)"
          @keydown.left.prevent="previousTab(index)"
          @keydown.right.prevent="nextTab(index)"
          @keydown.up.prevent="previousTab(index)"
          @keydown.down.prevent="nextTab(index)"
          >{{ tab.text }}</a
        >
      </li>
    </ul>
    <div class="grid-x full grid-margin-x search-header">
      <div class="cell large-auto stack-horizontally stack-align-middle">
        <cat-pagination
          :id="id"
          :paging-id="id + '-header'"
          :paging="paging"
          :pagination="pagination"
          :caption="pagingHeaderCaption"
          @ui-repeater-pagination-update="pageSelect"
        /><span v-if="pagination.total < 1">&nbsp;</span>
      </div>

      <div
        v-if="sortOptions || exportOptions"
        class="cell text-size-medium stack-horizontally stack-align-top large-shrink"
      >
        <span class="stack-horizontally stack-align-middle mrt"
          ><span class="mrx">{{
            resolveTxt("skin.results.sortby", "Sort by")
          }}</span>
          <inline-menu
            :id="id + '-sort-by'"
            small
            hollow
            button
            link
            :value="selectedSort"
            :items="sortOptions"
            :offscreen-label="resolveTxt('skin.results.sortby', 'Sort by')"
            :menu-button-id="id + '-sort-by-btn'"
            @ui-select="selectSort($event, id + '-sort-by-btn')"
          ></inline-menu>
        </span>
        <span
          v-if="exportOptions"
          class="stack-horizontally stack-align-middle"
        >
          <inline-menu
            :id="id + '-export'"
            small
            hollow
            button
            link
            :items="
              exportOptions.map(function (item) {
                return {
                  text: item.title,
                  value: item.type,
                  url: item.url,
                  isLink: item.navigationType === 'link'
                };
              })
            "
            :menu-button-id="id + '-export-btn'"
            :offscreen-label="
              resolveTxt('skin.results.export', 'Export results')
            "
            @ui-select="exportUrl($event)"
          >
            <template #trigger>
              <span>
                <i class="icon-doc-text"></i
                >{{ resolveTxt("skin.results.export", "Export results") }}
              </span>
            </template>
          </inline-menu>
        </span>
      </div>
    </div>
    <cat-errors v-if="errors" :errors="errors" />
    <div
      v-if="tabs"
      :id="id ? id + '_tab_panel' : null"
      class="cat-tab-panel"
      role="tabpanel"
      :aria-labelledby="panelLabelId"
    >
      <slot></slot>
    </div>
    <div v-else>
      <slot></slot>
    </div>
    <div v-if="!hideFooter" class="search-footer">
      <cat-pagination
        :id="id"
        :paging-id="id + '-footer'"
        :paging="paging"
        :pagination="pagination"
        :caption="pagingFooterCaption"
        @ui-repeater-pagination-update="pageSelect"
      /><span v-if="pagination.total < 1">&nbsp;</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatPagination from "@/components/catRepeater/catPagination.vue";
import InlineMenu from "@/components/catMenu/catMenu.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatSearchResults",
  components: {
    InlineMenu,
    CatErrors,
    CatPagination
  },
  props: {
    id: {
      type: [String, Number]
    },
    errors: [Array],
    paging: {
      type: Boolean,
      default: false
    },
    hideFooter: {
      type: Boolean,
      default: false
    },
    pagingHeaderCaption: {
      type: String,
      default: "Results page header",
      description: "Accessible name to identify the pagination header group."
    },
    pagingFooterCaption: {
      type: String,
      default: "Results page footer",
      description: "Accessible name to identify the pagination footer group."
    },
    pagination: [Object],
    tabs: [Array],
    sortOptions: {
      type: Array
    },
    exportOptions: {
      type: Array
    },
    exportName: {
      type: [String],
      default: "SearchExport"
    },
    exportTitle: {
      type: [String],
      default: "Export Results"
    },
    selectedSort: {
      type: Number,
      default: 0
    },
    selectedTab: [Number, String]
  },
  data: function () {
    return {
      orientation: "horizontal"
    };
  },
  methods: {
    pageSelect: function (event) {
      this.$emit("ui-repeater-pagination-update", event);
    },
    isSelected: function (value, index) {
      if (!this.selectedTab && index == 0) {
        return true;
      }
      if (typeof this.selectedTab === "number") {
        return this.selectedTab === index;
      }
      return this.selectedTab === value;
    },
    tabSelect: function (value, index) {
      this.$emit("ui-search-select", {
        value: value ? value : "",
        index: index
      });
    },
    _searchTab: function (value, index) {
      return {
        "search-tab": true,
        selected: this.isSelected(value, index)
      };
    },
    selectSize: function (event) {
      this.$emit("ui-repeater-pagination-update", {
        event: event,
        id: this.id,
        page: 1,
        size: event.value
      });
    },
    selectSort: function (obj, sourceId) {
      this.$emit("ui-sort-select", {
        value: obj.text,
        index: obj.value,
        sourceId: sourceId
      });
    },
    // exportUrl: function(exportOption) {
    //   // console.log("exportUrl:" + exportOption.url);
    //   return exportOption.url;
    // },
    exportUrl: function (exportOption) {
      window.location.href = exportOption.item.url;
    },
    nextTab: function (tabIndex) {
      const index = tabIndex === this.tabs.length - 1 ? 0 : tabIndex + 1;
      this.focusTab(index);
    },
    previousTab: function (tabIndex) {
      const index = tabIndex === 0 ? this.tabs.length - 1 : tabIndex - 1;
      this.focusTab(index);
    },
    focusTab: function (index) {
      jQuery(".cat-tab-" + index, this.$el)
        .first()
        .focus();
    }
  },
  computed: {
    pageSizes: function () {
      return this.pagination.sizes.map((el) => {
        return {
          text: el,
          value: el
        };
      });
    },
    panelLabelId: function () {
      let selectedIndex = 0;
      if (
        this.tabs &&
        this.tabs.some((tab, index) => {
          selectedIndex = index;
          return this.isSelected(tab.value, index);
        })
      ) {
        return this.id + "_tab_" + selectedIndex;
      }
      return null;
    }
  }
});
</script>
